import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { BehaviorSubject, map, Observable, switchMap, tap } from 'rxjs';
import { CouponApiService } from '../../services/coupon-api.service';
import { PhysicianProcedure } from '../../../../core/dtos/physician.dto';
import { PaginatorState } from 'primeng/paginator';
import { PaginationServiceInMemory } from 'app/core/services/pagination.service';
import { CouponFormsService } from '../../services/coupon-forms.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectionService } from 'app/core/services/selection.service';
import { CurrencyUtil } from 'app/core/utils/currency.utils';
import { ActivatedRoute, Router } from '@angular/router';
import { UserCoupon } from '../../dtos/user-coupon.dto';

@Component({
  selector: 'app-list-physician-procedures',
  templateUrl: './list-physician-procedures.component.html',
  styleUrl: './list-physician-procedures.component.scss',
})
export class ListPhysicianProceduresComponent implements OnInit {
  private couponApiService = inject(CouponApiService);
  private couponFormsService = inject(CouponFormsService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  protected procedureList$!: Observable<PhysicianProcedure[]>;
  protected searchText$ = new BehaviorSubject<string>('');
  protected paginationService =
    new PaginationServiceInMemory<PhysicianProcedure>(
      { currentPage: 1, items: [], offset: 10 },
      this.destroyRef,
    );
  protected formCouponProcedure = this.couponFormsService.formProcedures;
  protected formPhysician = this.couponFormsService.formPhysician;
  protected formAppointment = this.couponFormsService.formAppointment;
  protected formInfo = this.couponFormsService.formInfo;
  protected formUser = this.couponFormsService.formUser;
  protected selectionService = new SelectionService<PhysicianProcedure>();
  protected couponSelectionService = new SelectionService<UserCoupon>();
  protected convertToBrazilianCurrency =
    CurrencyUtil.convertToBrazilianCurrency;
  protected userCoupons: UserCoupon[] = [];

  ngOnInit(): void {
    this.toogleSelectProcedure = this.toogleSelectProcedure.bind(this);
    this.toggleSelectCoupon = this.toggleSelectCoupon.bind(this);
    this.procedureList$ = this.route.queryParamMap.pipe(
      switchMap(params => {
        const branchCode = params.get('branchCode') ?? '';
        const physicianId = params.get('physicianId') ?? '';

        return this.couponApiService
          .findPhysicianProcedures({
            branchCode,
            physicianId,
          })
          .pipe(
            map(({ response }) => response.procedures),
            switchMap(procedures =>
              this.searchText$.pipe(
                map(searchText =>
                  searchText
                    ? procedures.filter(procedure =>
                        procedure.procedureName
                          .toLowerCase()
                          .includes(searchText.toLowerCase()),
                      )
                    : procedures,
                ),
              ),
            ),
            tap(procedures => {
              this.paginationService.updateItems(procedures);
            }),
            switchMap(() => this.paginationService.connect()),
          );
      }),
    );

    this.formCouponProcedure.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (this.searchText$.value !== value.physicianProceduresSearch) {
          this.searchText$.next(value.physicianProceduresSearch ?? '');
        }
      });

    this.selectionService
      .getSelectedItems()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.formCouponProcedure.controls.selectedProcedures.setValue(result);
      });

    this.formUser.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadUserCoupons();
      });

    this.loadUserCoupons();
  }

  protected onPageChange(event: PaginatorState) {
    this.paginationService.goToPage((event.page || 0) + 1);
  }

  protected toogleSelectProcedure(procedureSelected: PhysicianProcedure) {
    this.selectionService.toggleSelection(
      procedureSelected.procedureId.toString(),
      procedureSelected,
    );
  }

  protected toggleSelectCoupon(couponSelected: UserCoupon) {
    this.couponSelectionService.toggleSelection(
      couponSelected.id,
      couponSelected,
    );
  }

  private loadUserCoupons() {
    const userData = this.formUser.value;
    const couponProcedures = userData.couponProcedures || [];

    this.userCoupons = couponProcedures as UserCoupon[];
  }

  protected getProceduresPrice(): Observable<{
    proceduresTotal: number;
    discountTotal: number;
    proceduresPriceWithTaxTotal: number;
    serviceTax: number;
    discountTotalWithTax: number;
  }> {
    return this.selectionService.getSelectedItems().pipe(
      map(items => ({
        proceduresTotal: items.reduce((acc, current) => {
          return acc + current.procedureOriginalPrice;
        }, 0),
        discountTotal: items.reduce(
          (previousValue, procedure) =>
            previousValue +
            (procedure.procedureOriginalPrice -
              procedure.procedureFinalPriceWithTax),
          0,
        ),
        proceduresPriceWithTaxTotal: items.reduce(
          (previousValue, procedure) =>
            previousValue + procedure.procedureFinalPriceWithTax,
          0,
        ),
        serviceTax: items.reduce(
          (previousValue, procedure) => previousValue + procedure.serviceTax,
          0,
        ),
        discountTotalWithTax: items.reduce(
          (previousValue, procedure) =>
            previousValue +
            (procedure.procedureOriginalPrice -
              procedure.procedureFinalPriceWithTax) +
            procedure.serviceTax,
          0,
        ),
      })),
    );
  }

  protected goBack(): void {
    this.router.navigate(['coupon/physician-list']);
  }

  protected hasSelectedCoupons(): boolean {
    return (
      this.couponSelectionService
        .getSelectedItems()
        .pipe(map(items => items.length > 0))
        .subscribe().closed || false
    );
  }

  protected getSelectedCouponsCount(): Observable<number> {
    return this.couponSelectionService
      .getSelectedItems()
      .pipe(map(items => items.length));
  }

  protected getSelectedProceduresCount(): Observable<number> {
    return this.selectionService
      .getSelectedItems()
      .pipe(map(items => items.length));
  }

  protected hasSelectedCoupons$(): Observable<boolean> {
    return this.couponSelectionService
      .getSelectedItems()
      .pipe(map(items => items.length > 0));
  }

  protected hasSelectedProcedures$(): Observable<boolean> {
    return this.selectionService
      .getSelectedItems()
      .pipe(map(items => items.length > 0));
  }

  protected goNext(): void {
    this.router.navigate(['coupon/purchase-summary']);
  }
}
